import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { BosDurum, Buton, Iskelet, Kart, NotKutusu, Rozet } from "@koclukpanel/ui";
import { ApiHatasi, personelYuku, type UzmanYuku, type YukSeviyesi } from "../api";

/**
 * UZMAN YÜKÜ VE KAPASİTESİ — F-PPL-12
 *
 * ================================================================
 * TEK BİR PUAN YOK — VE BU BİLİNÇLİ
 * ================================================================
 * Şartname bileşenleri sayıyor (*"aktif öğrenci · bu haftaki görüşme
 * sayısı · hazırlanacak program · bekleyen veli raporu · geciken görev
 * · ortalama rapor tamamlama süresi · izin durumu"*) ama **ağırlık
 * vermiyor.** `0.3 × öğrenci + 0.2 × görüşme + …` gibi bir formül,
 * hiçbir yerden gelmeyen katsayılarla üretilmiş bir sayıyı
 * "hesaplanmış" diye sunardı. İnsanlar tek sayıya güvenir; sahte
 * kesinlik, kesinlik yokluğundan tehlikelidir.
 *
 * Bu yüzden ekran yedi bileşeni **ham hâliyle** gösteriyor ve seviyeyi
 * her zaman **gerekçesiyle** yazıyor. Kararı veren kişi hangi
 * bileşenin kendisi için ağır olduğunu bilir; ekran onun yerine
 * karar vermiyor.
 */

const SEVIYE_ADI: Record<YukSeviyesi, string> = {
  uygun: "Uygun",
  dolu: "Dolu",
  asiri: "Aşırı yüklü",
  izinli: "İzinde",
};

const SEVIYE_TONU: Record<YukSeviyesi, "onay" | "uyari" | "hata" | "notr"> = {
  uygun: "onay",
  dolu: "uyari",
  asiri: "hata",
  izinli: "notr",
};

/**
 * Ortalama rapor süresi — saat ya da gün.
 *
 * `null` "—" olarak çiziliyor, "0 saat" DEĞİL: hiç rapor göndermemiş
 * bir uzmanı "anında gönderiyor" diye övmek, ölçümü tersine çevirirdi.
 */
export function raporSuresi(saat: number | null): string {
  if (saat === null) return "—";
  if (saat < 48) return `${Math.round(saat)} saat`;
  return `${(saat / 24).toFixed(1)} gün`;
}

/** Doluluk yüzdesi — kota yoksa "—". */
export function dolulukMetni(doluluk: number | null): string {
  return doluluk === null ? "—" : `%${Math.round(doluluk * 100)}`;
}

/** Tek ölçüm satırı — `dt`/`dd` çifti. */
function Olcum({ ad, deger, not }: { ad: string; deger: ReactNode; not?: string }) {
  return (
    <div>
      <dt className="text-notr-600 text-xs">{ad}</dt>
      <dd className="text-notr-900 text-lg font-semibold tabular-nums">{deger}</dd>
      {not && <p className="text-notr-600 text-xs">{not}</p>}
    </div>
  );
}

export function YukTablosu({ personelId }: { personelId: string }) {
  const veri = useQuery({
    queryKey: ["personel-yuk", personelId],
    queryFn: () => personelYuku(personelId),
    retry: false,
  });

  if (veri.isPending) return <Iskelet satir={4} />;

  if (veri.isError) {
    return (
      <Kart baslik="Yük ve kapasite">
        <BosDurum
          ton="hata"
          baslik="Yük bilgisi alınamadı"
          aciklama={veri.error instanceof ApiHatasi ? veri.error.message : undefined}
          eylem={
            <Buton gorunum="ikincil" onClick={() => void veri.refetch()}>
              Tekrar dene
            </Buton>
          }
        />
      </Kart>
    );
  }

  const yuk: UzmanYuku | null = veri.data;

  if (yuk === null) {
    /*
      KOÇ KAYDI OLMAYAN PERSONELDE TABLO ÇİZİLMİYOR. "0 öğrenci, kota
      0, %0 doluluk" göstermek, satış ya da finans çalışanı için
      olmayan bir ölçüyü varmış gibi sunmak olurdu — ve o satırlara
      bakan biri "bu kişi hiç çalışmıyor" diye okurdu.
    */
    return (
      <Kart baslik="Yük ve kapasite">
        <p className="text-notr-700 text-sm">
          Bu kişinin koç kaydı yok; öğrenci yükü ölçülmüyor. Kapasite tablosu yalnızca öğrenci
          atanan uzmanlar için tutulur.
        </p>
      </Kart>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Kart
        baslik="Yük ve kapasite"
        aciklama="Atama kararı verilirken bakılacak tablo." /* F-PPL-12 */
        eylem={<Rozet ton={SEVIYE_TONU[yuk.seviye]}>{SEVIYE_ADI[yuk.seviye]}</Rozet>}
      >
        <div className="flex flex-col gap-3">
          {/*
            GEREKÇE SEVİYENİN HEMEN ALTINDA. Rozet tek başına bir
            etiket; "neden dolu" sorusunu cevaplamayan bir etiket ya
            körü körüne güvenilir ya da hiç güvenilmez.
          */}
          <p role="status" className="text-notr-800 text-sm">
            {yuk.gerekce}
          </p>

          {/*
            ================================================================
            `SayiKarti` KULLANILMADI — VE SEBEBİ BU EKRANIN TAMAMI
            ================================================================
            O bileşen bir DURUM kartı: her sayıya "iyi / dikkat /
            kritik" atamayı zorunlu kılıyor ve etiketi her zaman
            çiziyor. "4 görüşme" için hangi hâli seçerdik? Hangisini
            seçersek seçelim, şartnamenin vermediği bir yargıyı
            ekranın ağzından vermiş olurduk — bu ekranın var olma
            sebebinin tam tersi.

            Yalın bir tanım listesi: sayılar sayı olarak duruyor,
            yorumu okuyan yapıyor.
          */}
          <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
            <Olcum
              ad="Aktif öğrenci"
              deger={`${yuk.aktifOgrenci}${yuk.kota > 0 ? ` / ${yuk.kota}` : ""}`}
              not={`Kota doluluğu ${dolulukMetni(yuk.doluluk)}`}
            />
            <Olcum ad="Bu hafta görüşme" deger={yuk.buHaftaGorusme} />
            <Olcum
              ad="Hazırlanacak program"
              deger={yuk.hazirlanacakProgram}
              not="Bu hafta için henüz yayınlanmamış"
            />
            <Olcum ad="Bekleyen veli raporu" deger={yuk.bekleyenVeliRapor} />
            <Olcum ad="Geciken görev" deger={yuk.gecikenGorev} />
            <Olcum
              ad="Ortalama rapor süresi"
              deger={raporSuresi(yuk.ortalamaRaporSaati)}
              not="Son 20 rapor · oluşturmadan gönderime"
            />
            <Olcum ad="İzin durumu" deger={yuk.izinli ? "İzinde" : "Çalışıyor"} />
          </dl>
        </div>
      </Kart>

      {/*
        SAYILARIN NASIL OKUNACAĞI EKRANDA YAZILI. Bir kapasite tablosu,
        okuyanın kendi ağırlıklarını koyabilmesi için var; "toplam yük
        şu kadar" diyen bir sayı vermek o kararı gizlice bizim vermemiz
        olurdu.
      */}
      <NotKutusu ton="bilgi">
        Bu tablo tek bir puana indirgenmiyor. Şartname yükün bileşenlerini sayıyor ama hangisinin
        daha ağır olduğunu söylemiyor — bir katsayı uydurmak, hiçbir yerden gelmeyen bir sayıyı
        “hesaplanmış” diye sunmak olurdu. Seviye yalnızca kuralı yazılı olan iki şeyden çıkıyor:
        kota ve izin durumu; ayrıca bekleyen iş belli bir sayıyı aşarsa kotada yer olsa bile “dolu”
        sayılıyor.
      </NotKutusu>
    </div>
  );
}
